import { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, GraduationCap, Loader2, Plus } from 'lucide-react';
import { trainingLogApi } from '../../services/trainingLogApi';
import {
    createNoTrainingConfirmer, formatConfirmMonth, type BannerState,
} from '../../utils/noTrainingConfirm';

interface Props {
    /** Validated YYYY-MM from the mail link (parseConfirmMonth), read by the page. */
    month: string;
    /** Opens the "add training" form. */
    onLogTraining: () => void;
    /** Bump after a training was saved or deleted: the banner reads the status again. */
    refreshKey?: number;
}

/**
 * Landing banner for /training-log?confirmNoTraining=YYYY-MM. Loading it only reads the status;
 * the confirmation is saved by the button click and nothing else.
 */
export function TrainingNoTrainingBanner({ month, onLogTraining, refreshKey = 0 }: Props) {
    const [state, setState] = useState<BannerState>({ kind: 'loading' });
    const confirmer = useRef<ReturnType<typeof createNoTrainingConfirmer> | null>(null);

    useEffect(() => {
        let alive = true;
        const c = createNoTrainingConfirmer(trainingLogApi, month, (s) => { if (alive) setState(s); });
        confirmer.current = c;
        void c.load();
        return () => { alive = false; };
    }, [month, refreshKey]);

    const label = formatConfirmMonth(month);
    const box = 'rounded-lg border p-4 mb-6 flex items-start gap-3';

    if (state.kind === 'loading') {
        return (
            <div className={`${box} bg-white border-gray-200 text-gray-600`} role="status">
                <Loader2 className="w-5 h-5 mt-0.5 animate-spin" />
                <span className="text-sm">Checking {label}...</span>
            </div>
        );
    }

    if (state.kind === 'load-error') {
        return (
            <div className={`${box} bg-red-50 border-red-200 text-red-800`} role="alert">
                <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm">
                    <p className="font-medium">Could not check {label}.</p>
                    <p>{state.message}</p>
                </div>
                <button
                    type="button"
                    onClick={() => void confirmer.current?.load()}
                    className="px-3 py-1.5 text-sm rounded-md border border-red-300 bg-white hover:bg-red-100"
                >
                    Retry
                </button>
            </div>
        );
    }

    if (state.kind === 'has-training') {
        const n = state.trainingCount;
        return (
            <div className={`${box} bg-green-50 border-green-200 text-green-800`} role="status">
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                <p className="text-sm">
                    Thank you, you have {n} {n === 1 ? 'training' : 'trainings'} logged for {label}. Nothing more to do.
                </p>
            </div>
        );
    }

    if (state.kind === 'confirmed') {
        return (
            <div className={`${box} bg-green-50 border-green-200 text-green-800`} role="status">
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm">
                    <p className="font-medium">Thank you, {label} is confirmed: no training.</p>
                    <p>If you attend a training later, you can still log it.</p>
                </div>
                <button
                    type="button"
                    onClick={onLogTraining}
                    className="px-3 py-1.5 text-sm rounded-md border border-green-300 bg-white hover:bg-green-100"
                >
                    Log a training
                </button>
            </div>
        );
    }

    if (state.kind === 'future') {
        return (
            <div className={`${box} bg-gray-50 border-gray-200 text-gray-700`} role="status">
                <GraduationCap className="w-5 h-5 mt-0.5 shrink-0" />
                <p className="flex-1 text-sm">
                    {label} has not started yet, so there is nothing to confirm. You can confirm once the month has begun.
                </p>
            </div>
        );
    }

    if (state.kind === 'closed') {
        return (
            <div className={`${box} bg-gray-50 border-gray-200 text-gray-700`} role="status">
                <GraduationCap className="w-5 h-5 mt-0.5 shrink-0" />
                <p className="flex-1 text-sm">
                    {label} can no longer be confirmed. Only the current and the previous month are open.
                </p>
                <button
                    type="button"
                    onClick={onLogTraining}
                    className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-100"
                >
                    Log a training
                </button>
            </div>
        );
    }

    const busy = state.kind === 'confirming';
    return (
        <div className={`${box} bg-blue-50 border-blue-200 text-blue-900`}>
            <GraduationCap className="w-5 h-5 mt-0.5 shrink-0" />
            <div className="flex-1">
                <p className="text-sm font-medium">Did you attend any training in {label}?</p>
                <p className="text-sm mb-3">
                    If you did, log it. If not, confirm with one click so we know there is nothing to report.
                </p>
                {state.kind === 'confirm-error' && (
                    <p className="text-sm text-red-700 mb-3 flex items-center gap-1.5" role="alert">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        Not saved: {state.message}
                    </p>
                )}
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => void confirmer.current?.confirm()}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        That&apos;s correct, no training in {label}
                    </button>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onLogTraining}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-blue-300 bg-white hover:bg-blue-100 disabled:opacity-60"
                    >
                        <Plus className="w-4 h-4" />
                        Log a training
                    </button>
                </div>
            </div>
        </div>
    );
}
